import { ActionIcon, Button, Skeleton, Tooltip } from '@mantine/core';
import {
  IconArrowLeft,
  IconCalendarStats,
  IconPencil,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { WINDOW_TONE } from './CertificateParts.js';
import classes from './Compliance.module.css';
import { surveyScheduleRows } from './certificate-view.js';
import type { SurveyKind, VesselSurvey } from './types.js';

/** Görünümün başlığı: başlık, "Back to Certificate List" ve eylemler. */
export function ViewHead({
  title,
  icon,
  onBack,
  actions,
}: {
  title: string;
  icon: ReactNode;
  onBack: () => void;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className={classes.viewHead}>
      <h2 className={classes.listTitle} style={{ margin: 0 }}>
        <span className={classes.linkIcon} aria-hidden>
          {icon}
        </span>{' '}
        {title}
      </h2>
      <div className={classes.viewActions}>
        {actions}
        <Button
          variant="default"
          size="xs"
          leftSection={<IconArrowLeft size={14} aria-hidden />}
          onClick={onBack}
        >
          {t('vesselCertificates.views.back')}
        </Button>
      </div>
    </div>
  );
}

export function ViewError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      tone="danger"
      title={t('vesselCertificates.error')}
      description={message}
      action={
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={onRetry}>
          {t('vesselCertificates.retry')}
        </Button>
      }
    />
  );
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className={classes.skeletonRows} aria-hidden>
      {Array.from({ length: rows }, (_, n) => n).map((n) => (
        <Skeleton key={n} height={18} />
      ))}
    </div>
  );
}

export interface SurveyScheduleProps {
  surveys: readonly VesselSurvey[] | null;
  error: string | null;
  onRetry: () => void;
  onBack: () => void;
  /** Ofis formu (K29); gemide yok. */
  onAdd?: ((kind: SurveyKind | null) => void) | undefined;
  onEdit?: ((survey: VesselSurvey) => void) | undefined;
}

/**
 * Survey Schedule (PMS belgesi §19): Annual, Intermediate, Special, Bottom, Boiler, Safety
 * Equipment, Radio; Last, Next, Window, Status, Report. Pencere girilmezse vadeden hesaplanır
 * (ADR 0029 D4); klas durum raporundaki pencere gerekçeyle yazılır.
 */
export function SurveySchedule({
  surveys,
  error,
  onRetry,
  onBack,
  onAdd,
  onEdit,
}: SurveyScheduleProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.surveys.${key}`, values);
  const rows = surveys ? surveyScheduleRows(surveys) : null;
  const kind = (value: string) => k(`kinds.${value}`, { defaultValue: value });
  return (
    <Panel>
      <ViewHead
        title={k('title')}
        icon={<IconCalendarStats size={18} />}
        onBack={onBack}
        actions={
          onAdd ? (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} aria-hidden />}
              onClick={() => onAdd(null)}
            >
              {k('add')}
            </Button>
          ) : null
        }
      />
      {error ? (
        <ViewError message={error} onRetry={onRetry} />
      ) : rows === null ? (
        <TableSkeleton />
      ) : (
        <section
          className={classes.tableBox}
          data-cards
          aria-label={k('table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={classes.table} data-cards>
            <thead>
              <tr>
                <th scope="col">{k('columns.survey')}</th>
                <th scope="col">{k('columns.last')}</th>
                <th scope="col">{k('columns.next')}</th>
                <th scope="col">{k('columns.window')}</th>
                <th scope="col">{k('columns.status')}</th>
                <th scope="col">{k('columns.report')}</th>
                {onEdit ? (
                  <th scope="col" data-center>
                    {k('columns.actions')}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ kind: rowKind, survey }, index) => (
                <tr key={survey?.id ?? `${rowKind}-${index}`}>
                  <td data-label={k('columns.survey')}>
                    <strong>{kind(rowKind)}</strong>
                  </td>
                  {survey ? (
                    <>
                      <td data-label={k('columns.last')} className={classes.nowrap}>
                        {formatDay(survey.lastDoneOn)}
                      </td>
                      <td data-label={k('columns.next')} className={classes.nowrap}>
                        {formatDay(survey.nextDueOn)}
                      </td>
                      <td data-label={k('columns.window')}>
                        <span className={classes.nowrap}>
                          {survey.windowFrom || survey.windowTo
                            ? `${formatDay(survey.windowFrom)} – ${formatDay(survey.windowTo)}`
                            : '—'}
                        </span>
                        {survey.overrideReason ? (
                          <span className={classes.nameNote}>
                            {k('overridden', { reason: survey.overrideReason })}
                          </span>
                        ) : null}
                      </td>
                      <td data-label={k('columns.status')}>
                        {survey.state ? (
                          <StatusBadge tone={WINDOW_TONE[survey.state]}>
                            {k(`states.${survey.state}`)}
                          </StatusBadge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td data-label={k('columns.report')}>
                        {survey.reportAttachmentKey ? k('reportOnFile') : '—'}
                      </td>
                    </>
                  ) : (
                    <td data-label={k('columns.last')} colSpan={5}>
                      <span className={classes.muted}>{k('notRecorded')}</span>
                    </td>
                  )}
                  {onEdit ? (
                    <td data-label={k('columns.actions')} data-center>
                      {survey ? (
                        <Tooltip label={k('edit', { kind: kind(rowKind) })} withinPortal>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={k('edit', { kind: kind(rowKind) })}
                            onClick={() => onEdit(survey)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                      ) : onAdd ? (
                        <Tooltip label={k('addKind', { kind: kind(rowKind) })} withinPortal>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={k('addKind', { kind: kind(rowKind) })}
                            onClick={() => onAdd(rowKind)}
                          >
                            <IconPlus size={16} />
                          </ActionIcon>
                        </Tooltip>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Panel>
  );
}
